/* Pattern D: bind the Liquid SVG before the renderer is ready, at every width. */
(() => {
  'use strict';
  const selector = '.merged-preview > svg[data-config-preview], .merged-preview > svg[data-necklace-preview]';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const entries = new Map();
  const suspended = new WeakSet();
  const NS = 'http://www.w3.org/2000/svg';
  let serial = 0;
  const moving = new Set();
  const group = className => {
    const node = document.createElementNS(NS, 'g');
    node.setAttribute('class', className);
    return node;
  };
  function observe(e) {
    e.observer.observe(e.svg, {subtree:true, childList:true, attributes:true, characterData:true});
  }
  function stop(e) {
    clearTimeout(e.timer);
    cancelAnimationFrame(e.heightRAF);
    e.completeHeight = null;
    e.cancelCompensation = null;
    moving.delete(e);
    if (!moving.size) document.documentElement.classList.remove('name-zoom-animating');
    e.snapshot?.remove();
    e.snapshot = null;
    e.bloom?.classList.remove('is-entering', 'is-returning');
    e.svg.classList.remove('is-zooming');
  }
  function finish(e) {
    if (e.disposed) return;
    e.observer.disconnect();
    try {
      e.completeHeight?.();
      e.observer.disconnect();
      stop(e);
      if (!e.expanded) {
        e.svg.style.removeProperty('height');
        e.svg.style.removeProperty('transition');
      }
    } finally { observe(e); }
  }
  function geometry(e) {
    e.svg.dataset.zoomScale = '1';
    e.svg.dataset.zoomHeightLimited = 'false';
    e.svg.dataset.zoomCapped = 'false';
    const view = e.svg.viewBox.baseVal;
    // Renderers may attach after bind; height:auto follows their final viewBox.
    if (view.width > 0 && view.height > 0) e.baseRatio = view.height / view.width;
    const rect = e.svg.getBoundingClientRect();
    const baseHeight = rect.width * e.baseRatio;
    const grow = !e.svg.hasAttribute('data-necklace-preview');
    const cap = Math.max(baseHeight, rect.width * (matchMedia('(max-width: 749px)').matches ? .62 : .5));
    e.baseHeight = baseHeight;
    e.targetHeight = baseHeight;
    const matrix = e.content.getCTM();
    if (!matrix || !view.width || !view.height || !Number.isFinite(matrix.a * matrix.d - matrix.b * matrix.c) || matrix.a * matrix.d === matrix.b * matrix.c) return null;
    const inverse = matrix.inverse();
    const points = [];
    e.bloom.querySelectorAll('.nb-name, .nb-necklace__name').forEach(name => {
      const b = name.getBBox(), m = inverse.multiply(name.getCTM());
      if (!b.width || !b.height) return;
      [[b.x,b.y],[b.x+b.width,b.y],[b.x,b.y+b.height],[b.x+b.width,b.y+b.height]]
        .forEach(([x,y]) => points.push(new DOMPoint(x,y).matrixTransform(m)));
    });
    if (!points.length) return null;
    const x = Math.min(...points.map(p => p.x)), y = Math.min(...points.map(p => p.y));
    const width = Math.max(...points.map(p => p.x)) - x;
    const height = Math.max(...points.map(p => p.y)) - y;
    // Measure in the unzoomed content coordinate system, even while expanded.
    // Account for preserveAspectRatio letterboxing: target the CSS frame width.
    if (!rect.width || !width || !height) return null;
    // Required CSS height at the width target, including 6% padding per side.
    const wanted = Math.max(baseHeight, rect.width * .65 * height / width / .88,
      rect.width * .65 * height / width + 16);
    e.targetHeight = grow ? Math.min(cap, wanted) : baseHeight;
    // Use the destination viewport, never an intermediate animated height.
    const px = Math.min(rect.width / view.width, e.targetHeight / view.height);
    const frameWidth = rect.width / px;
    const frameHeight = e.targetHeight / px;
    const padding = Math.max(8 / px, frameHeight * .06);
    const widthScale = frameWidth * .65 / width;
    const heightScale = (frameHeight - 2 * padding) / height;
    const scale = Math.max(1, Math.min(widthScale, heightScale));
    e.svg.dataset.zoomScale = String(scale);
    e.svg.dataset.zoomHeightLimited = String(heightScale < widthScale - .000001);
    e.svg.dataset.zoomCapped = String(grow && wanted > cap + .01);
    return `translate(${view.x+view.width/2-(x+width/2)*scale}px, ${view.y+view.height/2-(y+height/2)*scale}px) scale(${scale})`;
  }
  function wrap(e) {
    if (e.content?.parentNode === e.svg && e.bloom?.parentNode === e.content) return;
    // Theme-editor clones can include an old wrapper, but renderers keep the SVG.
    e.svg.querySelectorAll(':scope > .name-zoom-snapshot').forEach(node => node.remove());
    const stale = e.svg.querySelector(':scope > .name-zoom-content');
    if (stale) {
      const bloom = stale.querySelector(':scope > .name-zoom-bloom');
      stale.replaceWith(...(bloom || stale).childNodes);
    }
    let hit = e.svg.querySelector(':scope > .name-zoom-hit');
    if (!hit) {
      hit = document.createElementNS(NS, 'rect');
      hit.setAttribute('class', 'name-zoom-hit');
      hit.setAttribute('x', '-100%'); hit.setAttribute('y', '-100%');
      hit.setAttribute('width', '300%'); hit.setAttribute('height', '300%');
      hit.setAttribute('fill', 'transparent');
      hit.setAttribute('pointer-events', 'all');
      e.svg.prepend(hit);
    }
    e.content = group('name-zoom-content');
    e.bloom = group('name-zoom-bloom');
    e.bloom.addEventListener('animationend', event => {
      if (event.target === e.bloom) finish(e);
    }, {signal:e.listeners.signal});
    [...e.svg.childNodes].filter(node => node.nodeName.toLowerCase() !== 'defs' && !node.classList?.contains('name-zoom-hit'))
      .forEach(node => e.bloom.append(node));
    e.content.append(e.bloom);
    e.svg.append(e.content);
  }
  function freeze(e) {
    const snapshot = group('name-zoom-snapshot');
    snapshot.setAttribute('aria-hidden', 'true');
    // Freeze both visible layers on rapid reversal. Definitions stay on the root
    // SVG so gradients/clips remain valid without duplicate IDs or renderer refs.
    [...e.svg.children].filter(node => node.tagName.toLowerCase() !== 'defs' && !node.classList.contains('name-zoom-hit')).forEach(node => {
      const copy = node.cloneNode(true);
      const originals = [node, ...node.querySelectorAll('*')];
      const copies = [copy, ...copy.querySelectorAll('*')];
      originals.forEach((original, index) => {
        copies[index].removeAttribute('id');
        if (original.matches('.name-zoom-bloom, .name-zoom-snapshot')) {
          const css = getComputedStyle(original);
          Object.assign(copies[index].style, {animation:'none', transform:css.transform,
            transformOrigin:css.transformOrigin, opacity:css.opacity, filter:css.filter, willChange:'auto'});
        }
      });
      snapshot.append(copy);
    });
    return snapshot;
  }
  // Solve cubic-bezier(.22,.61,.36,1) for time; height and bloom share a clock.
  function ease(t) {
    const cubic = (u, a, b) => 3*(1-u)*(1-u)*u*a + 3*(1-u)*u*u*b + u*u*u;
    let lo = 0, hi = 1;
    for (let i = 0; i < 18; i++) {
      const mid = (lo + hi) / 2;
      if (cubic(mid, .22, .36) < t) lo = mid; else hi = mid;
    }
    return cubic((lo + hi) / 2, .61, 1);
  }
  function animateHeight(e, from, anchorTop) {
    const target = e.expanded ? e.targetHeight : e.baseHeight;
    const duration = reduced.matches ? 180 : e.expanded ? 700 : 500;
    const started = performance.now();
    const motion = {anchor:e.anchorKind || 'svg',boundary_events:0,user_interrupted:false,duration};
    e.svg._nameZoomMotion = motion;
    let compensate = motion.anchor === 'toggle' && !e.toggle.hidden && !e.userInterrupted;
    e.cancelCompensation = () => { compensate = false; e.userInterrupted = true; motion.user_interrupted = true; };
    moving.add(e);
    document.documentElement.classList.add('name-zoom-animating');
    const write = height => {
      e.observer.disconnect();
      e.svg.style.height = `${height}px`;
      if (compensate && !e.toggle.hidden) {
        const delta = e.toggle.getBoundingClientRect().top - anchorTop;
        // Disable native anchoring/smooth scrolling for this animation only.
        // Record clamping at either document boundary instead of accumulating it.
        window.scrollBy({top:delta,left:0,behavior:'instant'});
        if (Math.abs(e.toggle.getBoundingClientRect().top - anchorTop) > 1) motion.boundary_events++;
      }
      observe(e);
    };
    e.completeHeight = () => {
      write(target);
      e.completeHeight = null;
      e.cancelCompensation = null;
    };
    write(from);
    const tick = now => {
      const progress = Math.min(1, (now - started) / duration);
      write(from + (target - from) * ease(progress));
      if (progress < 1) e.heightRAF = requestAnimationFrame(tick);
      else finish(e);
    };
    e.heightRAF = requestAnimationFrame(tick);
  }
  function update(e, animate = false) {
    if (e.disposed) return;
    e.observer.disconnect();
    try {
      const fromHeight = e.svg.getBoundingClientRect().height;
      const anchorTop = e.toggle.getBoundingClientRect().top;
      const snapshot = animate && e.content?.isConnected ? freeze(e) : null;
      stop(e);
      wrap(e);
      const zoom = geometry(e);
      const useful = Boolean(zoom && Number(e.svg.dataset.zoomScale) >= 1.15);
      if (e.expanded && !useful) e.expanded = false;
      e.toggle.hidden = !useful;
      e.toggle.disabled = !useful;
      e.svg.dataset.zoomAvailable = String(useful);
      const pressed = Boolean(e.expanded && useful);
      e.content.style.transform = e.expanded && zoom ? zoom : 'translate(0px, 0px) scale(1)';
      e.toggle.setAttribute('aria-pressed', String(pressed));
      let label = e.toggle.querySelector('span');
      if (!label) { label = document.createElement('span'); e.toggle.append(label); }
      label.textContent = pressed
        ? window.CariamText("copy.text_253", 'もう一度タップで全体を表示') : window.CariamText("copy.text_254", 'タップで名前を拡大');
      e.toggle.querySelector('.name-zoom-plus').style.display = pressed ? 'none' : '';
      if (!e.toggle.isConnected) e.figure.insertBefore(e.toggle, e.figure.querySelector('figcaption'));
      if (snapshot && zoom) {
        e.snapshot = snapshot;
        e.svg.append(snapshot);
        e.svg.classList.add('is-zooming');
        e.bloom.classList.add(e.expanded ? 'is-entering' : 'is-returning');
        snapshot.classList.add(e.expanded ? 'is-leaving-full' : 'is-leaving-zoom');
        if (!e.svg.hasAttribute('data-necklace-preview')) animateHeight(e, fromHeight, anchorTop);
        // CSS controls the duration; the timer also cleans up in background tabs.
        const duration = parseFloat(getComputedStyle(e.bloom).animationDuration) * 1000;
        e.timer = setTimeout(() => finish(e), duration + 30);
      } else if (e.expanded && !e.svg.hasAttribute('data-necklace-preview')) {
        e.svg.style.height = `${e.targetHeight}px`;
      } else if (!e.expanded) {
        e.svg.style.removeProperty('height');
        e.svg.style.removeProperty('transition');
      }
    } finally { observe(e); }
  }
  function dispose(e) {
    e.disposed = true;
    e.observer.disconnect(); stop(e); e.listeners.abort(); e.resize.disconnect();
    e.toggle.remove(); e.cancelCompensation = null; e.completeHeight = null;
    e.svg.style.removeProperty('height'); e.svg.style.removeProperty('transition');
    entries.delete(e.svg);
  }
  function bind(svg) {
    if (entries.has(svg) || suspended.has(svg)) return;
    const figure = svg.parentElement;
    figure.querySelectorAll('.name-zoom-toggle, .name-zoom-helper').forEach(node => node.remove());
    const toggle = document.createElement('button');
    toggle.type = 'button'; toggle.className = 'name-zoom-toggle';
    const icon = document.createElementNS(NS, 'svg');
    for (const [key, value] of Object.entries({'aria-hidden':'true', focusable:'false', viewBox:'0 0 24 24'})) icon.setAttribute(key, value);
    for (const [tag, attrs] of [['circle', {cx:'10', cy:'10', r:'6.5'}], ['path', {d:'m15 15 5 5M7 10h6'}], ['path', {class:'name-zoom-plus', d:'M10 7v6'}]]) {
      const node = document.createElementNS(NS, tag);
      for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, value);
      icon.append(node);
    }
    toggle.append(icon, document.createElement('span'));
    // Section clones may carry the original ID; always assign a unique control target.
    do { svg.id = `name-preview-${++serial}`; }
    while ([...document.querySelectorAll('[id]')].some(node => node !== svg && node.id === svg.id));
    toggle.setAttribute('aria-controls', svg.id);
    // Editor clones may contain a mid-zoom inline height from the source SVG.
    svg.style.removeProperty('height'); svg.style.removeProperty('transition');
    const rect = svg.getBoundingClientRect();
    const view = svg.viewBox.baseVal;
    const e = {svg,figure,toggle,expanded:false,baseRatio:rect.width ? rect.height / rect.width : view.height / view.width,
      lastWidth:rect.width,listeners:new AbortController()};
    entries.set(svg,e);
    const flip = event => { if (!toggle.disabled && !toggle.hidden) {
      e.userInterrupted = false;
      e.anchorKind = event.currentTarget === toggle ? 'toggle' : 'svg';
      e.expanded = !e.expanded; update(e,true);
    } };
    toggle.addEventListener('click', flip, {signal:e.listeners.signal});
    svg.addEventListener('click', flip, {signal:e.listeners.signal});
    for (const type of ['wheel', 'touchmove']) window.addEventListener(type, () => e.cancelCompensation?.(),
      {passive:true,signal:e.listeners.signal});
    e.observer = new MutationObserver(() => update(e, e.expanded || Boolean(e.snapshot)));
    e.resize = new ResizeObserver(() => {
      const width = svg.getBoundingClientRect().width;
      // Height transitions are ours. React only to external width changes.
      if (Math.abs(width - e.lastWidth) < .5) return;
      e.lastWidth = width;
      update(e, e.expanded);
    });
    e.resize.observe(svg);
    update(e);
  }
  function scan() {
    entries.forEach(e => {
      if (!e.svg.isConnected || e.svg.parentElement !== e.figure) dispose(e);
    });
    document.querySelectorAll(selector).forEach(bind);
  }
  function start() {
    scan();
    // Observe the actual page root, including late rendering and editor sections
    // outside <main>. Readiness attributes are observed on each SVG from bind().
    new MutationObserver(scan).observe(document.body, {childList:true,subtree:true});
    document.addEventListener('shopify:section:load', event => {
      event.target.querySelectorAll(selector).forEach(svg => suspended.delete(svg));
      scan();
    });
    document.addEventListener('shopify:section:unload', event => {
      entries.forEach(e => { if (event.target.contains(e.svg)) { suspended.add(e.svg); dispose(e); } });
    });
    const refreshFonts = () => entries.forEach(e => update(e, e.expanded));
    document.fonts?.ready.then(refreshFonts);
    document.fonts?.addEventListener('loadingdone', refreshFonts);
  }
  reduced.addEventListener('change', () => entries.forEach(e => update(e)));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
  else start();
})();
